import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { EditModalControls } from '@nucleus-commerce/admin-kit/forms';

import type { SearchEnginesTab, SharedProductSections } from '@/features/products';

import type {
  StoreProductFormInput,
  StoreProductFormOutput,
} from '../schemas/store-product-form-schema';

type StoreForm = UseFormReturn<StoreProductFormInput, unknown, StoreProductFormOutput>;
type MasterForm = SharedProductSections['form'];

export interface SearchEnginesModal {
  show: () => void;
  modal: EditModalControls['modal'];
  /** The tab holding a field error, so the modal never hides one behind the other tab. */
  errorTab: SearchEnginesTab | undefined;
}

/**
 * `useEditModal` over two forms at once: the listing tab holds this store's fields, the Summary
 * and FAQ tab the master product's. Done keeps both for the save bar, Cancel restores both, and a
 * failed save on any of them opens the modal on its tab.
 */
export function useSearchEnginesModal(
  store: StoreForm,
  master: MasterForm | undefined,
): SearchEnginesModal {
  const [open, setOpen] = useState(false);
  const remember = () =>
    structuredClone({
      handle: store.getValues('handle'),
      seo: store.getValues('seo'),
      summary: master?.getValues('summary'),
      faq: master?.getValues('faq'),
    });
  const [snapshot, setSnapshot] = useState<ReturnType<typeof remember> | null>(null);

  const storeErrors = store.formState.errors;
  const masterErrors = master?.formState.errors;
  const errorTab: SearchEnginesTab | undefined =
    storeErrors.handle || storeErrors.seo
      ? 'listing'
      : masterErrors?.summary || masterErrors?.faq
        ? 'answers'
        : undefined;
  const hasError = errorTab !== undefined;
  const [openedForError, setOpenedForError] = useState(false);
  if (hasError !== openedForError) {
    setOpenedForError(hasError);
    if (hasError && !open) {
      setSnapshot(remember());
      setOpen(true);
    }
  }

  const show = () => {
    setSnapshot(remember());
    setOpen(true);
  };
  const cancel = () => {
    if (snapshot) {
      store.setValue('handle', snapshot.handle, { shouldDirty: true });
      store.setValue('seo', snapshot.seo, { shouldDirty: true });
      if (snapshot.summary !== undefined) {
        master?.setValue('summary', snapshot.summary, { shouldDirty: true });
      }
      if (snapshot.faq !== undefined) master?.setValue('faq', snapshot.faq, { shouldDirty: true });
    }
    store.clearErrors(['handle', 'seo']);
    master?.clearErrors(['summary', 'faq']);
    setOpen(false);
  };
  const done = async () => {
    const [listing, answers] = await Promise.all([
      store.trigger(['handle', 'seo']),
      master ? master.trigger(['summary', 'faq']) : true,
    ]);
    if (listing && answers) setOpen(false);
  };

  return {
    show,
    errorTab,
    modal: {
      open,
      onOpenChange: (next) => {
        if (next) show();
        else cancel();
      },
      onDone: () => void done(),
    },
  };
}
